import { redactBody } from './redactResponse';
import { getOrderedChildren, getFolderAuthChain } from './collectionFolders';
import { sendRequest } from './tauri';
import { withUserNetwork } from './requestNetworkOptions';
import { resolveDynamicVariable } from './dynamicVariables';
import { createVariableResolver } from './resolveVariables';
import { runScript } from './scriptSandbox';
import { applyRequestMutations, preRequestScriptBody } from './scriptRequestMutations';
import {
  buildScriptVariableMap,
  withheldSecretKeys,
  isScriptWithheld,
} from './scriptVariables';
import { withheldSecretValues } from './sandboxRequestRedaction';
import { getFormDataFieldsFromRequest } from './multipart';
import { assembleHttpRequest, collectionAuthOf } from './requestAssembly';
import { scriptChainFor } from './scriptChain';
import { loadRunnableCollection } from './runnableCollection';
import { folderVariablesFor } from './folderVariables';
import { lookupScopedVariable } from './variablePrecedence';
import { recordFeature, ACTION } from '../services/telemetry';

/** Flatten requests in collection tree order (optionally scoped to a folder subtree). */
export function flattenCollectionRequests(collection, { folderId = null } = {}) {
  const results = [];
  function traverse(parentId) {
    for (const child of getOrderedChildren(collection, parentId ?? null)) {
      if (child.type === 'request') results.push(child.item);
      else traverse(child.id);
    }
  }
  if (folderId) traverse(folderId);
  else traverse(null);
  return results;
}

function makeSubstituteVariables({
  localVariables,
  folderVariables,
  collectionVariables,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
}) {
  return createVariableResolver(
    (name) => {
      // Run-scoped locals (boo.variables.set from any script in this run) are
      // the highest-priority scope — same as RequestBuilder's
      // scriptLocalVarsRef — and are what makes request chaining work.
      if (localVariables && localVariables[name] !== undefined) return localVariables[name];
      // Stored scopes in THE order (utils/variablePrecedence); the folder
      // chain (nearest first) sits directly above collection.
      const hit = lookupScopedVariable(name, {
        folder: folderVariables,
        collection: collectionVariables,
        environment: selectedEnvironment?.variables,
        workspace: workspaceVariables,
        global: globalVariables,
      });
      if (hit) return hit.variable.value;
      return null;
    },
    { fallback: resolveDynamicVariable }
  );
}

/**
 * Scopes a script may see, in the shape `scriptVariables` expects. Kept as one
 * function so the runner and RequestBuilder cannot disagree about which scopes
 * a script gets (COL-02).
 */
function scriptScopes(collectionVariables, selectedEnvironment, workspaceVariables, globalVariables, folderVariables) {
  return {
    globalVariables,
    workspaceVariables,
    environmentVariables: selectedEnvironment?.variables,
    collectionVariables,
    folderVariables,
  };
}

/** Apply envMutations back to selectedEnvironment variable array (in-place mutation of the runner's local copy). */
function applyEnvMutations(selectedEnvironment, envMutations) {
  if (!envMutations || !selectedEnvironment) return;
  for (const [key, value] of Object.entries(envMutations)) {
    if (!selectedEnvironment.variables) selectedEnvironment.variables = [];
    const existing = selectedEnvironment.variables.find((v) => v.key === key);
    // A script may not overwrite or delete a withheld secret even in the
    // runner's throwaway copy: `makeSubstituteVariables` reads that copy, so a
    // successful write would put an attacker-chosen value where the user's
    // secret was for every remaining request in the run.
    if (existing && isScriptWithheld(existing)) continue;
    if (value === null) {
      selectedEnvironment.variables = selectedEnvironment.variables.filter((v) => v.key !== key);
    } else if (existing) {
      // REPLACE, never `existing.value = value`. `runCollectionRequests`
      // builds `mutableEnv` with `variables: [...variables]` — a copy of the
      // ARRAY whose entries are still the caller's objects, so mutating one in
      // place wrote straight back into the environment the store holds, and
      // from there to disk and to teammates. The runner was only ever half a
      // local copy; this is the other half (COL-03).
      selectedEnvironment.variables = selectedEnvironment.variables.map((v) =>
        v.key === key ? { ...v, value } : v
      );
    } else {
      selectedEnvironment.variables.push({ key, value, enabled: true });
    }
  }
}

/** Parse Set-Cookie header strings into a name→value map (strips attributes). */
function parseCookies(setCookieHeaders) {
  const jar = {};
  const headers = Array.isArray(setCookieHeaders) ? setCookieHeaders : [setCookieHeaders];
  for (const h of headers) {
    if (!h) continue;
    const pair = h.split(';')[0].trim();
    const eq = pair.indexOf('=');
    if (eq < 1) continue;
    const name = pair.slice(0, eq).trim();
    const value = pair.slice(eq + 1).trim();
    if (name) jar[name] = value;
  }
  return jar;
}

/**
 * Run all requests in a collection sequentially.
 * Returns an array of { request, status, statusText, time, passed, tests, error }.
 */
export async function runCollectionRequests({
  collection,
  selectedEnvironment,
  collectionVariables = [],
  workspaceVariables = [],
  globalVariables = [],
  folderId = null,
  onProgress,
  signal,
  // Cloud REST workspaces: the sidebar only has the request INDEX (no body,
  // headers, auth or scripts) — see utils/runnableCollection.
  fetchFullCollection,
}) {
  const sidebarRequests = flattenCollectionRequests(collection, { folderId });
  if (sidebarRequests.length === 0) return [];
  try {
    collection = await loadRunnableCollection(collection, { fetchFullCollection });
  } catch (err) {
    // Never run the index: every request would go out empty and "fail" for a
    // reason that has nothing to do with the API under test.
    const error = `Could not load the collection's requests: ${err?.message || 'request failed'}`;
    return sidebarRequests.map((request) => ({
      request, status: null, statusText: '', time: 0, passed: false, tests: [], error,
    }));
  }
  const requests = flattenCollectionRequests(collection, { folderId });

  // Mutable copies so script variable mutations propagate between requests
  const mutableEnv = selectedEnvironment ? { ...selectedEnvironment, variables: [...(selectedEnvironment.variables || [])] } : null;
  let mutableCollectionVars = [...collectionVariables];
  // boo.variables.set() from pre/test scripts, alive for the whole run.
  const runLocalVars = {};

  const getSubstituteVariables = (folderVariables) => makeSubstituteVariables({
    localVariables: runLocalVars,
    folderVariables,
    collectionVariables: mutableCollectionVars,
    selectedEnvironment: mutableEnv,
    workspaceVariables,
    globalVariables,
  });

  // `settings.collection_auth` — `collection.auth` is not a field the app writes.
  const collectionAuth = collectionAuthOf(collection);
  const results = [];
  // Cookie jar: hostname → { name: value }
  const cookieJar = {};

  for (let i = 0; i < requests.length; i++) {
    if (signal?.aborted) break;

    const req = requests[i];
    const startTime = Date.now();
    // Per request: two requests of one run can sit in different folders.
    const folderVars = folderVariablesFor(collection, req.folder_id);

    try {
      // req.setHeader() & co. from each script of the chain, applied in order
      // right before the send (untrusted; validated in scriptRequestMutations).
      const chainMutations = [];
      // Pre-request scripts: collection → folders → request (Postman's order).
      for (const { script: preScript } of scriptChainFor(collection, req, 'pre')) {
        const scopes = scriptScopes(mutableCollectionVars, mutableEnv, workspaceVariables, globalVariables, folderVars);
        const scriptResult = await runScript(preScript, {
          environmentVariables: buildScriptVariableMap(scopes),
          localVariables: runLocalVars,
          withheldKeys: withheldSecretKeys(scopes),
          withheldValues: withheldSecretValues(scopes),
          // name/id back pm.info.requestName / pm.info.requestId; the other two
          // script call sites pass them too, so the three cannot drift.
          request: {
            url: req.url || '',
            method: req.method || 'GET',
            headers: {},
            name: req.name || '',
            id: req.id ?? req.originalId ?? '',
            // as typed (unsubstituted), as Bruno's req.getBody() sees it
            body: preRequestScriptBody({ ...req, formDataFields: getFormDataFieldsFromRequest(req) }),
          },
          response: null,
          // pm.sendRequest — the imported-Postman token flow; same sender as Send.
          sendHttp: (r) => sendRequest(withUserNetwork(r)),
        });
        if (scriptResult.errors?.length) {
          throw new Error(`Pre-request script error: ${scriptResult.errors[0]}`);
        }
        Object.assign(runLocalVars, scriptResult.variables || {});
        if (scriptResult.envMutations) applyEnvMutations(mutableEnv, scriptResult.envMutations);
        if (scriptResult.requestMutations) chainMutations.push(scriptResult.requestMutations);
      }

      // Re-derive substituteVariables after pre-request mutations
      const subVars = getSubstituteVariables(folderVars);

      // The SAME assembly Send uses (utils/requestAssembly) — body, headers,
      // cookie rows, params, folder → collection auth.
      const assembled = await assembleHttpRequest(
        { ...req, formDataFields: getFormDataFieldsFromRequest(req) },
        {
          substituteVariables: subVars,
          folderAuthChain: getFolderAuthChain(collection, req.folder_id),
          collectionAuth,
        }
      );
      // Script changes go on after substitution and auth and are never
      // substituted again; there is no URL change (req.setUrl refuses) — the
      // same rules as Send (RequestBuilder).
      const { method, headers: mutatedHeaders, body: requestBody, multipart } = applyRequestMutations(
        { method: assembled.method, headers: assembled.headers, body: assembled.body, multipart: assembled.multipart },
        chainMutations
      );
      const authedHeaders = { ...mutatedHeaders };
      const authedUrl = assembled.url;

      // Inject cookies from the run's jar for this host
      try {
        const host = new URL(authedUrl).hostname;
        const jarCookies = cookieJar[host];
        if (jarCookies && Object.keys(jarCookies).length > 0) {
          const existing = authedHeaders['Cookie'] || authedHeaders['cookie'] || '';
          const jarStr = Object.entries(jarCookies).map(([n, v]) => `${n}=${v}`).join('; ');
          authedHeaders['Cookie'] = existing ? `${existing}; ${jarStr}` : jarStr;
        }
      } catch { /* invalid URL — skip cookie injection */ }

      const response = await sendRequest(withUserNetwork({
        method,
        url: authedUrl,
        headers: authedHeaders,
        body: requestBody,
        multipart,
      }));

      const elapsed = Date.now() - startTime;

      // Store any Set-Cookie headers in the jar
      const setCookie = response.headers?.['set-cookie'] || response.headers?.['Set-Cookie'];
      if (setCookie) {
        try {
          const host = new URL(authedUrl).hostname;
          cookieJar[host] = { ...(cookieJar[host] || {}), ...parseCookies(setCookie) };
        } catch { /* ignore */ }
      }

      // Test script
      let tests = [];
      let scriptRan = false;
      // Test scripts: collection → folders → request; assertions accumulate.
      for (const { script: testScript } of scriptChainFor(collection, req, 'test')) {
        scriptRan = true;
        recordFeature(ACTION.TEST_SCRIPT);   // identifier-free; once a day at most
        const scopes = scriptScopes(mutableCollectionVars, mutableEnv, workspaceVariables, globalVariables, folderVars);
        const scriptResult = await runScript(testScript, {
          environmentVariables: buildScriptVariableMap(scopes),
          localVariables: runLocalVars,
          withheldKeys: withheldSecretKeys(scopes),
          withheldValues: withheldSecretValues(scopes),
          request: {
            url: authedUrl,
            method,
            headers: authedHeaders,
            name: req.name || '',
            id: req.id ?? req.originalId ?? '',
          },
          response: {
            status: response.status,
            data: response.data,
            headers: response.headers || {},
            time: response.time ?? elapsed,
          },
        });
        if (scriptResult.errors?.length) {
          tests = [...tests, { name: 'Script error', passed: false, error: scriptResult.errors[0] }];
        } else {
          tests = [...tests, ...(scriptResult.tests || [])];
          Object.assign(runLocalVars, scriptResult.variables || {});
          if (scriptResult.envMutations) applyEnvMutations(mutableEnv, scriptResult.envMutations);
        }
      }

      // When a script ran but produced no assertions, fall back to HTTP status
      // (mutation-only scripts are valid). `scriptRan` distinguishes this from
      // "no test script at all" for accurate UI reporting.
      const passed = tests.length > 0
        ? tests.every((t) => t.passed)
        : response.status >= 100 && response.status < 400;

      results.push({
        request: req,
        status: response.status,
        statusText: response.status_text || '',
        time: response.time ?? elapsed,
        passed,
        tests,
        scriptRan,
        // Kept for AI run-debugging (P2): a failing request's body is surfaced to
        // the model. Only read for failures downstream, so this is not sent wholesale.
        responseBody: response.data,
        error: null,
      });
    } catch (err) {
      const elapsed = Date.now() - startTime;
      results.push({
        request: req,
        status: null,
        statusText: '',
        time: elapsed,
        passed: false,
        tests: [],
        error: err.message || 'Request failed',
      });
    }

    onProgress?.({
      index: i,
      total: requests.length,
      request: req,
      results: [...results],
    });
  }

  return results;
}

/** Max characters of a failed request's body surfaced for AI debugging. */
const RUN_FAIL_BODY_SNIPPET = 1500;

function bodySnippet(data) {
  if (data == null) return null;
  const raw = typeof data === 'string' ? data : JSON.stringify(data);
  // Redact BEFORE clipping (13.09.2026 review N1): a clip that cuts a token in
  // half leaves a tail too short for the value patterns to match, and JSON keys
  // like "password" are only caught by the structured pass.
  const text = redactBody(raw).body;
  return text.length > RUN_FAIL_BODY_SNIPPET ? `${text.slice(0, RUN_FAIL_BODY_SNIPPET)}…[truncated]` : text;
}

/** Format runner results for AI tool responses. */
export function formatCollectionRunResult(collection, results, { interactive = false, cancelled = false } = {}) {
  const passed = results.filter((r) => r.passed).length;
  const failed = results.length - passed;
  return {
    success: !cancelled && failed === 0,
    cancelled,
    collection_id: collection.id,
    collection_name: collection.name,
    total: results.length,
    passed_count: passed,
    failed_count: failed,
    total_time_ms: results.reduce((acc, r) => acc + (r.time || 0), 0),
    interactive,
    results: results.map((r) => {
      const base = {
        request_id: r.request.id,
        name: r.request.name,
        method: r.request.method,
        url: r.request.url,
        status: r.status,
        time_ms: r.time,
        passed: r.passed,
        tests: r.tests || [],
        error: r.error,
      };
      // Attach a body snippet only for failures (for AI diagnosis; bounded size).
      if (!r.passed) base.response_body_snippet = bodySnippet(r.responseBody);
      return base;
    }),
  };
}
